import React, { useState, useEffect, useRef } from "react";
import MainScreen from "../../components/MainScreen";
import { Button, Form, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, selectUser } from "../../features/userSlice";
import Loading from "../../components/Loading";
import ErrorMessage from "../../components/ErrorMessage";
import "./User.css";
import axios from "axios";
import "./Paginated1.css"; // cards + floating stack
import "./Paginated2.css"; // minimal (errors only)
import { FaHome } from "react-icons/fa";

const ManageUsers = () => {
  const [Users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    emp_id: "",
    Name: "",
    email_id: "",
    mobile_no: "",
    password: "",
    Created_By: "",
    Created_Date: "",
    Modified_By: "",
    Modified_Date: "",
    IsActive: "Y", // 🔹 default Yes
  });

  const user = useSelector(selectUser);
  const emp_id = user?.emp_id;
  const token = user?.token;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const UserIdRef = useRef(null);
  const topRef = useRef(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error1, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    const filtered = (Users || []).filter((u) =>
      (u?.Name || "").toLowerCase().includes((searchTerm || "").toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, Users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // adjust to your auth style (headers/body)
      const response = await axios.post("/api/Users/getAllUsers", { token });
      const UsersData = Array.isArray(response.data) ? response.data : [response.data];
      setUsers(UsersData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching Users:", error);
      setLoading(false);
    }
  };

  const handleAddNewRecord = () => {
    setSelectedUser(null);
    clearForm();
    setErrors({});
    setMessage("");
    setTimeout(() => UserIdRef.current?.focus(), 0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDateTimeForInput = (dateTime) => {
    if (!dateTime) return "";
    const d = new Date(dateTime);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const convertUTCToLocalTime = (utcDateTime) => {
    if (!utcDateTime) return "";
    const d = new Date(utcDateTime);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  };

  const clearForm = () => {
    setFormData({
      emp_id: "",
      Name: "",
      email_id: "",
      mobile_no: "",
      password: "",
      Created_By: "",
      Created_Date: "",
      Modified_By: "",
      Modified_Date: "",
      IsActive: "Y", // 🔹 reset to default Yes
    });
  };

  // Uppercase for emp_id + normal for others
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "emp_id") {
      setFormData((prev) => ({ ...prev, emp_id: (value || "").toUpperCase() }));
      return;
    }

    // For IsActive we just store "Y" or "N" from the <select>
    if (name === "IsActive") {
      setFormData((prev) => ({ ...prev, IsActive: value === "N" ? "N" : "Y" }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.emp_id) newErrors.emp_id = "User ID is required.";
    if (!formData.Name) newErrors.User_Name = "User Name is required.";
    else if (formData.Name.length > 50) newErrors.User_Name = "User Name cannot exceed 50 characters.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setMessage("Please fix the validation errors before submitting.");
      return;
    }

    setError(false);
    setLoading(true);

    try {
      const baseData = { ...formData };
      let result;

      if (selectedUser) {
        const updatedFormData1 = {
          ...baseData,
          Modified_By: emp_id,
          Modified_Date: new Date().toISOString(),
        };
        result = await axios.put(`/api/Users/updateUser/${selectedUser.emp_id}`, updatedFormData1);
      } else {
        const updatedFormData1 = {
          ...baseData,
          Created_By: emp_id,
          Created_Date: new Date().toISOString(),
          Modified_By: null,
          Modified_Date: null,
        };
        result = await axios.post(`/api/Users/register`, updatedFormData1);
      }

      if (result?.data) {
        setShowSuccessModal(true);
        fetchUsers();
        setSelectedUser(null);
        clearForm();
        setErrors({});
        setMessage("");
      } else if (result?.error) {
        setShowErrorModal(true);
        setError(true);
        setMessage(result.error.message || "Submission failed.");
      }
      setLoading(false);
    } catch (error) {
      setShowErrorModal(true);
      setError(true);
      setLoading(false);
      console.error("Submit Error:", error);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setErrors({});
    setMessage("");
    navigate("/manageUsers");
  };

  const handleUserClick = (u) => {
    setErrors({});
    setMessage("");
    setFormData({
      emp_id: String(u.emp_id ?? "").toUpperCase(),
      Name: u.Name ?? "",
      email_id: u.email_id ?? "",
      password: u.password ?? "",
      mobile_no: u.mobile_no ?? "",
      Created_By: u.Created_By ?? "",
      Modified_By: u.Modified_By ?? "",
      Created_Date: formatDateTimeForInput(u.Created_Date),
      Modified_Date: formatDateTimeForInput(u.Modified_Date),
      IsActive: (u.IsActive === "N" ? "N" : "Y"), // 🔹 load current state (fallback Y)
    });
    setSelectedUser(u);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCancel = () => {
    const hasValues = Object.values(formData).some((v) => v);
    if (hasValues) {
      const ok = window.confirm("Discard changes?");
      if (!ok) return;
    }
    clearForm();
    setSelectedUser(null);
    scrollToTop();
  };

  if (!token) {
    return (
      <div className="mobileContainer">
        <p>You must be logged in to manage users.</p>
        <Button onClick={() => navigate("/")}>Go to Login</Button>
      </div>
    );
  }

  return (
    <MainScreen title="Manage Users">
      <div ref={topRef} />

      {/* Search */}
      <div className="toolbar toolbar--single">
        <Form.Control
          type="text"
          placeholder="Search by name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="toolbarInput"
        />
      </div>

      {/* Cards */}
      <div className="cardsSection">
        <div className="cardsScroll">
          {loading && <div className="cardSkeleton">Loading…</div>}

          {!loading && filteredUsers.length === 0 && (
            <div className="noRecords">No users found.</div>
          )}

          {!loading && filteredUsers.length > 0 && (
            <div className="cardsGrid">
              {filteredUsers.map((u) => (
                <button
                  key={u.emp_id}
                  className="userCard"
                  onClick={() => handleUserClick(u)}
                >
                  <div className="userCardHeader">
                    <div className="userAvatar">
                      {String(u.Name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="userHeaderText">
                      <div className="userName">
                        {u.Name || "(No name)"}
                        {/* 🔹 status pill */}
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            padding: "2px 8px",
                            borderRadius: 12,
                            background: u.IsActive === "N" ? "#ffe6e6" : "#e7f7ec",
                            color: u.IsActive === "N" ? "#a40000" : "#0b6b2f",
                            border: `1px solid ${u.IsActive === "N" ? "#ffb3b3" : "#bfe7cc"}`
                          }}
                        >
                          {u.IsActive === "N" ? "Inactive" : "Active"}
                        </span>
                      </div>
                      <div className="userId">#{String(u.emp_id).toUpperCase()}</div>
                    </div>
                  </div>

                  <div className="userCardMeta">
                    {u.email_id && <div className="metaItem">{u.email_id}</div>}
                    {u.mobile_no && <div className="metaItem">{u.mobile_no}</div>}
                  </div>

                  <div className="userCardDates">
                    {u.Created_Date && (
                      <div className="dateItem">
                        <small>Created</small>
                        <span>{convertUTCToLocalTime(u.Created_Date)}</span>
                      </div>
                    )}
                    {u.Modified_Date && (
                      <div className="dateItem">
                        <small>Updated</small>
                        <span>{convertUTCToLocalTime(u.Modified_Date)}</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Floating stack: Top • Cancel • Add */}
      <div className="fabStack">
        <button
          className="fabBtn"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          title="Top"
        >
          ↑
        </button>

        <button
          className="fabBtn danger"
          onClick={handleCancel}
          aria-label="Cancel / Clear"
          title="Cancel"
        >
          ✕
        </button>

        <button
          className="fabBtn primary"
          onClick={handleAddNewRecord}
          aria-label="Add user"
          title="Add"
        >
          +
        </button>

        <button
          className="fabBtn primary"
          style={{paddingBottom:10}}
          onClick={() => navigate("/dashboard")}
        >
          <FaHome />
        </button>
      </div>

      {/* Form */}
      <div className="mobileForm">
        {error1 && <ErrorMessage variant="danger">Unable to Register, Please check data</ErrorMessage>}
        {message && <ErrorMessage variant="danger">{message}</ErrorMessage>}
        {loading && <Loading />}

        <Form onSubmit={submitHandler}>
          <Form.Group controlId="emp_id" className="formGroup">
            <Form.Label>User ID</Form.Label>
            <Form.Control
              type="text"
              inputMode="text"
              name="emp_id"
              value={formData.emp_id}
              placeholder="Enter User ID"
              onChange={handleChange}
              style={{ textTransform: "uppercase" }}
              ref={UserIdRef}
            />
            {errors.emp_id && <div className="error-message">{errors.emp_id}</div>}
          </Form.Group>

          <Form.Group controlId="UserName" className="formGroup">
            <Form.Label>User Name</Form.Label>
            <Form.Control
              type="text"
              name="Name"
              value={formData.Name}
              placeholder="Enter User Name"
              onChange={handleChange}
            />
            {errors.User_Name && <div className="error-message">{errors.User_Name}</div>}
          </Form.Group>

          <Form.Group controlId="email_id" className="formGroup">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email_id"
              value={formData.email_id}
              placeholder="Enter email"
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group controlId="mobile_no" className="formGroup">
            <Form.Label>Mobile No</Form.Label>
            <Form.Control
              type="tel"
              inputMode="tel"
              name="mobile_no"
              value={formData.mobile_no}
              placeholder="Enter mobile number"
              onChange={handleChange}
            />
          </Form.Group>

          {/* 🔹 NEW: IsActive Select */}
          <Form.Group controlId="IsActive" className="formGroup">
            <Form.Label>Is Active</Form.Label>
            <Form.Select
              name="IsActive"
              value={formData.IsActive}
              onChange={handleChange}
            >
              <option value="Y">Yes (Active)</option>
              <option value="N">No (Inactive)</option>
            </Form.Select>
          </Form.Group>

          <div className="submit-button">
            <Button variant="primary" type="submit">
              {selectedUser ? "Update" : "Submit"}
            </Button>
          </div>
        </Form>

        <div className="inlineLink">
          Existing User? <Link to="/login">Login Here</Link>
        </div>
      </div>

      {/* Modals */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Error</Modal.Title></Modal.Header>
        <Modal.Body>Unable to register</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowErrorModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSuccessModal} onHide={handleSuccessModalClose} centered>
        <Modal.Header closeButton><Modal.Title>Success</Modal.Title></Modal.Header>
        <Modal.Body>User saved successfully</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleSuccessModalClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    </MainScreen>
  );
};

export default ManageUsers;
